export type LiveEntryStep = 'authentication' | 'session' | 'live';

export type LiveEntryDecision =
  | { kind: 'loading' }
  | { kind: 'error'; step: LiveEntryStep }
  | { kind: 'speaker' }
  | { kind: 'attendee' };

export type LiveEntryRole = 'presenter' | 'speaker' | 'attendee' | undefined;

export interface LiveEntryInput {
  hasToken: boolean;
  /** Lien d'invitation : le jeton de rôle est demandé, les infos spectateur ne le sont pas. */
  isInviteFlow: boolean;
  roleRequested: boolean;
  roleLoading: boolean;
  roleError: boolean;
  role: LiveEntryRole;
  hasUserId: boolean;
  attendeeTried: boolean;
  attendeeLoading: boolean;
  attendeeError: boolean;
  hasAttendeeInfo: boolean;
  /** Filet : au-delà du délai, un chargement qui n'aboutit pas devient une erreur actionnable. */
  timedOut: boolean;
}

export function decideLiveEntry(input: LiveEntryInput): LiveEntryDecision {
  const {
    hasToken,
    isInviteFlow,
    roleRequested,
    roleLoading,
    roleError,
    role,
    hasUserId,
    attendeeTried,
    attendeeLoading,
    attendeeError,
    hasAttendeeInfo,
    timedOut,
  } = input;

  const stillLoading = (): LiveEntryDecision =>
    timedOut ? { kind: 'error', step: 'authentication' } : { kind: 'loading' };

  if (!hasToken) {
    return { kind: 'error', step: 'authentication' };
  }

  // Les deux parcours sont exclusifs : chacun a son propre état terminal, jamais la conjonction
  // des deux, sans quoi l'échec de l'unique appel utile ne sort jamais du chargement.
  if (isInviteFlow) {
    if (role === 'speaker' && hasUserId) {
      return { kind: 'speaker' };
    }
    if (hasAttendeeInfo) {
      return { kind: 'attendee' };
    }
    if (roleError) {
      return { kind: 'error', step: 'authentication' };
    }
    if (!roleRequested || roleLoading) {
      return stillLoading();
    }
    // Jeton d'invitation résolu sur un rôle que cette porte ne sait pas router.
    return { kind: 'error', step: 'authentication' };
  }

  if (hasAttendeeInfo) {
    return { kind: 'attendee' };
  }
  if (attendeeError) {
    return { kind: 'error', step: 'authentication' };
  }
  if (!attendeeTried || attendeeLoading) {
    return stillLoading();
  }
  return stillLoading();
}

/**
 * Un écran d'attente cède la place à un état terminal passé ce délai, même si aucune requête ne
 * se termine ni n'échoue.
 */
export const LIVE_ENTRY_TIMEOUT_MS = 20000;

export interface LiveEntryPendingInput {
  isReady: boolean;
  status?: string;
}

/**
 * Vraie tant que la page spectateur doit patienter. À utiliser à la fois pour armer le filet de
 * temps et pour décider du rendu : si les deux conditions divergent, l'écran d'attente survit au
 * filet et redevient infini.
 */
export function isLiveEntryPending({ isReady, status }: LiveEntryPendingInput): boolean {
  return !isReady || status === 'UPCOMING';
}
